import { lazy } from 'react';
import PropTypes from 'prop-types';
import { createBrowserRouter } from 'react-router-dom';
import { RootLayout } from './RootLayout';
import { ProtectedRoute } from './ProtectedRoutes';
import { PublicRouteGuard } from './PublicRouteGuard';
import { TermsProvider } from '../contexts/TermsContext';

// ─── Lazy page imports ─────────────────────────────────────────────────────────
// Páginas relacionadas compartilham um único barril em chunks/ → um import() por
// grupo = um chunk. Múltiplas chamadas lazy() apontando para o mesmo módulo fazem
// webpack criar UM chunk; quando um carrega, todos do grupo estão disponíveis.

// Auth
const Login          = lazy(() => import('./chunks/auth').then(m => ({ default: m.Login })));
const Idiomas        = lazy(() => import('./chunks/auth').then(m => ({ default: m.Idiomas })));
const ForgotPassword = lazy(() => import('./chunks/auth').then(m => ({ default: m.ForgotPassword })));
const SignUp         = lazy(() => import('./chunks/auth').then(m => ({ default: m.SignUp })));
const CreatePassword = lazy(() => import('./chunks/auth').then(m => ({ default: m.CreatePassword })));
const Success        = lazy(() => import('./chunks/auth').then(m => ({ default: m.Success })));
const LoginPassword  = lazy(() => import('./chunks/auth').then(m => ({ default: m.LoginPassword })));
const SignUpOtp      = lazy(() => import('./chunks/auth').then(m => ({ default: m.SignUpOtp })));

// Bill Payment
const BillPayment           = lazy(() => import('./chunks/bill-payment').then(m => ({ default: m.BillPayment })));
const BillPaymentConfirmation = lazy(() => import('./chunks/bill-payment').then(m => ({ default: m.BillPaymentConfirmation })));
const BillPaymentPix        = lazy(() => import('./chunks/bill-payment').then(m => ({ default: m.BillPaymentPix })));
const BillPaymentHistory    = lazy(() => import('./chunks/bill-payment').then(m => ({ default: m.BillPaymentHistory })));
const BillPaymentReceipt    = lazy(() => import('./chunks/bill-payment').then(m => ({ default: m.BillPaymentReceipt })));
const BillPaymentProcessing = lazy(() => import('./chunks/bill-payment').then(m => ({ default: m.BillPaymentProcessing })));
const BillPaymentError      = lazy(() => import('./chunks/bill-payment').then(m => ({ default: m.BillPaymentError })));

// Training (normal + protected)
const Training                = lazy(() => import('./chunks/training').then(m => ({ default: m.Training })));
const TrainingDetail          = lazy(() => import('./chunks/training').then(m => ({ default: m.TrainingDetail })));
const TrainingQuiz            = lazy(() => import('./chunks/training').then(m => ({ default: m.TrainingQuiz })));
const ProtectedTraining       = lazy(() => import('./chunks/training').then(m => ({ default: m.ProtectedTraining })));
const ProtectedTrainingDetail = lazy(() => import('./chunks/training').then(m => ({ default: m.ProtectedTrainingDetail })));
const ProtectedTrainingQuiz   = lazy(() => import('./chunks/training').then(m => ({ default: m.ProtectedTrainingQuiz })));

// Quiz (normal + protected)
const Quiz               = lazy(() => import('./chunks/quiz').then(m => ({ default: m.Quiz })));
const QuizDetail         = lazy(() => import('./chunks/quiz').then(m => ({ default: m.QuizDetail })));
const QuizQuestions      = lazy(() => import('./chunks/quiz').then(m => ({ default: m.QuizQuestions })));
const ProtectedQuiz      = lazy(() => import('./chunks/quiz').then(m => ({ default: m.ProtectedQuiz })));
const ProtectedQuizDetail = lazy(() => import('./chunks/quiz').then(m => ({ default: m.ProtectedQuizDetail })));
const ProtectedQuizQuiz  = lazy(() => import('./chunks/quiz').then(m => ({ default: m.ProtectedQuizQuiz })));

// Survey
const Survey          = lazy(() => import('./chunks/survey').then(m => ({ default: m.Survey })));
const SurveyDetail    = lazy(() => import('./chunks/survey').then(m => ({ default: m.SurveyDetail })));
const SurveyQuestions = lazy(() => import('./chunks/survey').then(m => ({ default: m.SurveyQuestions })));

// Stamp & Benefits
const StampRedemption            = lazy(() => import('./chunks/stamp').then(m => ({ default: m.StampRedemption })));
const Stamp                      = lazy(() => import('./chunks/stamp').then(m => ({ default: m.Stamp })));
const StampRedemptionSuccess     = lazy(() => import('./chunks/stamp').then(m => ({ default: m.StampRedemptionSuccess })));
const StampRedemptionSuccessInfo = lazy(() => import('./chunks/stamp').then(m => ({ default: m.StampRedemptionSuccessInfo })));
const StampRedemptionError       = lazy(() => import('./chunks/stamp').then(m => ({ default: m.StampRedemptionError })));
const BenefitsHistory            = lazy(() => import('./chunks/stamp').then(m => ({ default: m.BenefitsHistory })));

// Performance & Ranking
const Performance             = lazy(() => import('./chunks/performance').then(m => ({ default: m.Performance })));
const PerformanceNew          = lazy(() => import('./chunks/performance').then(m => ({ default: m.PerformanceNew })));
const PerformanceNewAccordion = lazy(() => import('./chunks/performance').then(m => ({ default: m.PerformanceNewAccordion })));
const PerformanceUnified      = lazy(() => import('./chunks/performance').then(m => ({ default: m.PerformanceUnified })));
const Ranking                 = lazy(() => import('./chunks/performance').then(m => ({ default: m.Ranking })));

// Campaigns
const CampaignsList         = lazy(() => import('./chunks/campaign').then(m => ({ default: m.CampaignsList })));
const CampaignDetail        = lazy(() => import('./chunks/campaign').then(m => ({ default: m.CampaignDetail })));
const CampaignDetailResults = lazy(() => import('./chunks/campaign').then(m => ({ default: m.CampaignDetailResults })));
const CampaignSimulator     = lazy(() => import('./chunks/campaign').then(m => ({ default: m.CampaignSimulator })));
const CampaignSimulator2    = lazy(() => import('./chunks/campaign').then(m => ({ default: m.CampaignSimulator2 })));

// News
const News         = lazy(() => import('./chunks/news').then(m => ({ default: m.News })));
const NewsDetail   = lazy(() => import('./chunks/news').then(m => ({ default: m.NewsDetail })));
const NewsCategory = lazy(() => import('./chunks/news').then(m => ({ default: m.NewsCategory })));
const NewsAuthor   = lazy(() => import('./chunks/news').then(m => ({ default: m.NewsAuthor })));

// Games / Gamificação
const Raspadinha = lazy(() => import('./chunks/games').then(m => ({ default: m.Raspadinha })));
const Roleta     = lazy(() => import('./chunks/games').then(m => ({ default: m.Roleta })));
const Balao      = lazy(() => import('./chunks/games').then(m => ({ default: m.Balao })));
const Gol        = lazy(() => import('./chunks/games').then(m => ({ default: m.Gol })));

// Photo Gallery
const PhotoGallery = lazy(() => import('./chunks/photo').then(m => ({ default: m.PhotoGallery })));
const SendPhoto    = lazy(() => import('./chunks/photo').then(m => ({ default: m.SendPhoto })));
const PhotoDetail  = lazy(() => import('./chunks/photo').then(m => ({ default: m.PhotoDetail })));

// Account / Perfil
const Profile       = lazy(() => import('./chunks/account').then(m => ({ default: m.Profile })));
const FirstAccess   = lazy(() => import('./chunks/account').then(m => ({ default: m.FirstAccess })));
const DeleteAccount = lazy(() => import('./chunks/account').then(m => ({ default: m.DeleteAccount })));
const AcceptedTerms = lazy(() => import('./chunks/account').then(m => ({ default: m.AcceptedTerms })));
const TermsView     = lazy(() => import('./chunks/account').then(m => ({ default: m.TermsView })));

// Informativas / Regras
const Mechanics        = lazy(() => import('./chunks/info').then(m => ({ default: m.Mechanics })));
const Winners          = lazy(() => import('./chunks/info').then(m => ({ default: m.Winners })));
const HowToParticipate = lazy(() => import('./chunks/info').then(m => ({ default: m.HowToParticipate })));
const HowItWorks       = lazy(() => import('./chunks/info').then(m => ({ default: m.HowItWorks })));
const Contact          = lazy(() => import('./chunks/info').then(m => ({ default: m.Contact })));

// Redirects / SSO
const SSOCallback         = lazy(() => import('./chunks/redirects').then(m => ({ default: m.SSOCallback })));
const RevvoRedirect       = lazy(() => import('./chunks/redirects').then(m => ({ default: m.RevvoRedirect })));
const MarketplaceRedirect = lazy(() => import('./chunks/redirects').then(m => ({ default: m.MarketplaceRedirect })));
const Take5Redirect       = lazy(() => import('./chunks/redirects').then(m => ({ default: m.Take5Redirect })));
const Token               = lazy(() => import('./chunks/redirects').then(m => ({ default: m.Token })));

// Social / Comunidade
const MissionsPage     = lazy(() => import('./chunks/social').then(m => ({ default: m.MissionsPage })));
const MinhaEquipe      = lazy(() => import('./chunks/social').then(m => ({ default: m.MinhaEquipe })));
const CheckIn          = lazy(() => import('./chunks/social').then(m => ({ default: m.CheckIn })));
const IndicacaoPage    = lazy(() => import('./chunks/social').then(m => ({ default: m.IndicacaoPage })));
const ReferralRedirect = lazy(() => import('./chunks/social').then(m => ({ default: m.ReferralRedirect })));
const Invite           = lazy(() => import('./chunks/social').then(m => ({ default: m.Invite })));

// ─── Standalone (alto tráfego ou dependências distintas) ──────────────────────
const FAQ         = lazy(() => import('@/pages/FAQ'));
const LoggedFAQ   = lazy(() => import('@/pages/FAQ/LoggedFAQ'));
const ContactForm = lazy(() => import('@/pages/ContactForm'));
const Destination = lazy(() => import('@/pages/Destination'));
const Home        = lazy(() => import('@/pages/Home'));
const Statement   = lazy(() => import('@/pages/Statement'));
const ListWallet  = lazy(() => import('@/pages/ListWallet'));
const TourGuide   = lazy(() => import('@/pages/TourGuide'));
const Logout      = lazy(() => import('@/pages/Logout'));
const NotFound    = lazy(() => import('@/pages/NotFound'));
const Wait        = lazy(() => import('@/pages/Wait'));
const Error       = lazy(() => import('@/pages/Error'));
const TestAuth    = lazy(() => import('@/pages/TestAuth'));
const TimerDemo   = lazy(() => import('@/pages/TimerDemo/TimerDemo'));
const Docs        = lazy(() => import('@/pages/Docs'));
const Segmentation  = lazy(() => import('@/pages/Segmentation'));
const Tierization   = lazy(() => import('@/pages/Tierization'));
// ChatBot usa export nomeado → extraímos o default manualmente
const ChatBot     = lazy(() => import('@/pages/Chatbot').then(m => ({ default: m.ChatBot })));
const InboxPage   = lazy(() => import('@/pages/Inbox'));

// ─── Wrapper de Terms ──────────────────────────────────────────────────────────
const TermsWrapper = ({ children }) => (
  <TermsProvider>{children}</TermsProvider>
);

TermsWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

// ─── Router ────────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: <PublicRouteGuard><Login /></PublicRouteGuard>
      },
      {
        path: '/auth/login',
        element: <PublicRouteGuard><Login /></PublicRouteGuard>
      },
      {
        path: '/home',
        element: <TermsWrapper><ProtectedRoute><Home /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/tour-guide',
        element: <TermsWrapper><ProtectedRoute><TourGuide /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/auth/login/password',
        element: <PublicRouteGuard><LoginPassword /></PublicRouteGuard>
      },
      {
        path: '/select-destination',
        element: <TermsWrapper><ProtectedRoute><Destination /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/language',
        element: <PublicRouteGuard><TermsWrapper><Idiomas /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/faq',
        element: <PublicRouteGuard><FAQ /></PublicRouteGuard>
      },
      {
        path: '/fale-conosco',
        element: <PublicRouteGuard allowIfSSO><ContactForm /></PublicRouteGuard>
      },
      {
        path: '/faq/logged',
        element: <ProtectedRoute><LoggedFAQ /></ProtectedRoute>
      },
      {
        path: '/chatbot/:id',
        element: <ProtectedRoute><ChatBot /></ProtectedRoute>
      },
      {
        path: '/auth/forgot-password',
        element: <PublicRouteGuard><ForgotPassword /></PublicRouteGuard>
      },
      {
        path: '/auth/signup',
        element: <PublicRouteGuard><SignUp /></PublicRouteGuard>
      },
      {
        path: '/auth/create-password',
        element: <PublicRouteGuard><CreatePassword /></PublicRouteGuard>
      },
      {
        path: '/auth/signup-otp',
        element: <PublicRouteGuard><SignUpOtp /></PublicRouteGuard>
      },
      {
        path: '/auth/success',
        element: <PublicRouteGuard><Success /></PublicRouteGuard>
      },
      {
        path: '/dashboard',
        element: <TermsWrapper><ProtectedRoute><Home /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/auth/logout',
        element: <ProtectedRoute><Logout /></ProtectedRoute>
      },
      {
        path: '/profile',
        element: <TermsWrapper><ProtectedRoute><Profile /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/inbox',
        element: <TermsWrapper><ProtectedRoute><InboxPage /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/first-access',
        element: <TermsWrapper><ProtectedRoute><FirstAccess /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/statement',
        element: <TermsWrapper><ProtectedRoute><Statement /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/statement/:walletId',
        element: <TermsWrapper><ProtectedRoute><Statement /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/wallets',
        element: <TermsWrapper><ProtectedRoute><ListWallet /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/benefits',
        element: <TermsWrapper><ProtectedRoute><StampRedemption /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/benefits-history',
        element: <TermsWrapper><ProtectedRoute><BenefitsHistory /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/wait',
        element: <PublicRouteGuard><TermsWrapper><Wait /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/pay-bill',
        element: <PublicRouteGuard><TermsWrapper><BillPayment /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/pay-bill-history',
        element: <PublicRouteGuard><TermsWrapper><BillPaymentHistory /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/pay-bill-receipt/:id',
        element: <PublicRouteGuard><TermsWrapper><BillPaymentReceipt /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/pay-bill-processing/:id',
        element: <PublicRouteGuard><TermsWrapper><BillPaymentProcessing /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/pay-bill-error/:id',
        element: <PublicRouteGuard><TermsWrapper><BillPaymentError /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/pay-bill-pix',
        element: <PublicRouteGuard><TermsWrapper><BillPaymentPix /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/pay-bill-confirmation',
        element: <PublicRouteGuard><TermsWrapper><BillPaymentConfirmation /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/stamp-redemption',
        element: <PublicRouteGuard><TermsWrapper><StampRedemption /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/stamp/:id',
        element: <PublicRouteGuard><TermsWrapper><Stamp /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/stamp-redemption-success/:id',
        element: <TermsWrapper><ProtectedRoute><StampRedemptionSuccess /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/stamp/:id/success-info',
        element: <TermsWrapper><ProtectedRoute><StampRedemptionSuccessInfo /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/stamp/:id/error',
        element: <TermsWrapper><ProtectedRoute><StampRedemptionError /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/test-auth',
        element: <PublicRouteGuard><TestAuth /></PublicRouteGuard>
      },
      {
        path: '/accepted-terms',
        element: <TermsWrapper><ProtectedRoute><AcceptedTerms /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/terms/:id',
        element: <TermsWrapper><ProtectedRoute><TermsView /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/delete-account',
        element: <TermsWrapper><ProtectedRoute><DeleteAccount /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/mechanics',
        element: <TermsWrapper><ProtectedRoute><Mechanics /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/winners',
        element: <TermsWrapper><ProtectedRoute><Winners /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/how-to-participate',
        element: <TermsWrapper><ProtectedRoute><HowToParticipate /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/campaigns',
        element: <TermsWrapper><ProtectedRoute><Performance /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/campaigns-list',
        element: <TermsWrapper><ProtectedRoute><CampaignsList /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/contact',
        element: <TermsWrapper><ProtectedRoute><Contact /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/how-it-works',
        element: <TermsWrapper><ProtectedRoute><HowItWorks /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/campaign/:id',
        element: <TermsWrapper><ProtectedRoute><CampaignDetail /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/campaign/:id/results',
        element: <TermsWrapper><ProtectedRoute><CampaignDetailResults /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/performance',
        element: <TermsWrapper><ProtectedRoute><Performance /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/performance-new/:id',
        element: <TermsWrapper><ProtectedRoute><PerformanceNew /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/desempenho',
        element: <TermsWrapper><ProtectedRoute><PerformanceUnified /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/desempenho/:id',
        element: <TermsWrapper><ProtectedRoute><PerformanceUnified /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/performance-list/:id',
        element: <TermsWrapper><ProtectedRoute><PerformanceNewAccordion /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/ranking',
        element: <TermsWrapper><ProtectedRoute><Ranking /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/segmentation',
        element: <TermsWrapper><ProtectedRoute><Segmentation /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/tierization',
        element: <TermsWrapper><ProtectedRoute><Tierization /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/training',
        element: <TermsWrapper><ProtectedRoute><Training /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/training/:id',
        element: <TermsWrapper><ProtectedRoute><TrainingDetail /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/training/:id/quiz',
        element: <TermsWrapper><ProtectedRoute><TrainingQuiz /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/quiz',
        element: <TermsWrapper><ProtectedRoute><Quiz /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/quiz/:id',
        element: <TermsWrapper><ProtectedRoute><QuizDetail /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/quiz/:id/questions',
        element: <TermsWrapper><ProtectedRoute><QuizQuestions /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/survey',
        element: <TermsWrapper><ProtectedRoute><Survey /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/survey/:id',
        element: <TermsWrapper><ProtectedRoute><SurveyDetail /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/survey/:id/questions',
        element: <TermsWrapper><ProtectedRoute><SurveyQuestions /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/protected-training',
        element: <TermsWrapper><ProtectedRoute><ProtectedTraining /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/protected-training/:id',
        element: <TermsWrapper><ProtectedRoute><ProtectedTrainingDetail /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/protected-training/:id/quiz',
        element: <TermsWrapper><ProtectedRoute><ProtectedTrainingQuiz /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/protected-quiz',
        element: <TermsWrapper><ProtectedRoute><ProtectedQuiz /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/protected-quiz/:id',
        element: <TermsWrapper><ProtectedRoute><ProtectedQuizDetail /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/protected-quiz/:id/quiz',
        element: <TermsWrapper><ProtectedRoute><ProtectedQuizQuiz /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/timer-demo',
        element: <TermsWrapper><ProtectedRoute><TimerDemo /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/news',
        element: <PublicRouteGuard><TermsWrapper><News /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/news/:slug',
        element: <PublicRouteGuard><TermsWrapper><NewsDetail /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/news/category/:categoryId',
        element: <PublicRouteGuard><TermsWrapper><NewsCategory /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/news/author/:authorId',
        element: <PublicRouteGuard><TermsWrapper><NewsAuthor /></TermsWrapper></PublicRouteGuard>
      },
      {
        path: '/docs',
        element: <TermsWrapper><ProtectedRoute><Docs /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/campaign-simulator',
        element: <TermsWrapper><ProtectedRoute><CampaignSimulator /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/campaign-simulator-2',
        element: <TermsWrapper><ProtectedRoute><CampaignSimulator2 /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/raspadinha',
        element: <TermsWrapper><ProtectedRoute><Raspadinha /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/roleta',
        element: <TermsWrapper><ProtectedRoute><Roleta /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/balao',
        element: <TermsWrapper><ProtectedRoute><Balao /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/gol',
        element: <TermsWrapper><ProtectedRoute><Gol /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/missions',
        element: <TermsWrapper><ProtectedRoute><MissionsPage /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/minha-equipe',
        element: <TermsWrapper><ProtectedRoute><MinhaEquipe /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/check-in/:campaignId',
        element: <TermsWrapper><ProtectedRoute><CheckIn /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/photo-gallery',
        element: <TermsWrapper><ProtectedRoute><PhotoGallery /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/photo-gallery/send',
        element: <TermsWrapper><ProtectedRoute><SendPhoto /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/photo-gallery/send/:challengeId',
        element: <TermsWrapper><ProtectedRoute><SendPhoto /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/photo-gallery/photo/:id',
        element: <TermsWrapper><ProtectedRoute><PhotoDetail /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/photo-gallery/:challengeId',
        element: <TermsWrapper><ProtectedRoute><PhotoGallery /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/r/:code',
        element: <ReferralRedirect />
      },
      {
        path: '/indique-e-ganhe',
        element: <TermsWrapper><ProtectedRoute><IndicacaoPage /></ProtectedRoute></TermsWrapper>
      },
      {
        path: '/invite/:id',
        element: <Invite />
      },
      {
        path: '/404',
        element: <TermsWrapper><NotFound /></TermsWrapper>
      },
      {
        path: '*',
        element: <TermsWrapper><NotFound /></TermsWrapper>
      },
      {
        path: '/redirect/revvo',
        element: <RevvoRedirect />,
        isPrivate: true
      },
      {
        path: '/redirect/marketplace',
        element: <MarketplaceRedirect />,
        isPrivate: true
      },
      {
        path: '/redirect/take5',
        element: <ProtectedRoute><Take5Redirect /></ProtectedRoute>,
        isPrivate: true
      },
      {
        path: '/error',
        element: <PublicRouteGuard><Error /></PublicRouteGuard>,
        isPrivate: false
      },
      {
        path: '/sso-allianz',
        element: <PublicRouteGuard allowIfSSO={true}><SSOCallback /></PublicRouteGuard>,
        isPrivate: false
      },
      {
        path: '/sso-subway',
        element: <PublicRouteGuard allowIfSSO={true}><SSOCallback /></PublicRouteGuard>,
        isPrivate: false
      },
      {
        path: '/sso-tigre',
        element: <PublicRouteGuard allowIfSSO={true}><SSOCallback /></PublicRouteGuard>,
        isPrivate: false
      },
      {
        path: '/token.html',
        element: <PublicRouteGuard allowIfSSO={true}><Token /></PublicRouteGuard>,
        isPrivate: false
      },
      {
        path: '/token',
        element: <PublicRouteGuard allowIfSSO={true}><Token /></PublicRouteGuard>,
        isPrivate: false
      },
    ],
  },
]);
